import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  Button,
  Column,
  Columns,
  Divider,
  Heading,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';
import React from 'react';

import { PriceCurrencyTax } from 'src/app/views/components/PriceCurrencyTax';
import { BenefitList } from 'src/app/views/shared/BenefitList';
import type { Money, Price, Tax } from 'src/graphql/generated';

import translations from './.vocab';

import * as styles from './index.css';

type UpsellPanelProps = {
  disabled?: boolean;
  heading: string;
  price?: Money;
  tax?: Tax;
  priceDescription?: string;
  benefits: Array<string>;
  columnWidth?: '1/2' | '3/5';
  image: string;
  imageClassName?: string;
  imageDescription?: string;
  isSelected: boolean;
  divider?: boolean;
  onClick: () => void;
  productPrice?: Price;
};

export const UpsellPanel = ({
  heading,
  price,
  tax,
  priceDescription,
  benefits,
  columnWidth = '1/2',
  image,
  imageClassName,
  imageDescription,
  divider = true,
  isSelected = false,
  onClick,
  productPrice,
}: UpsellPanelProps) => {
  const { t } = useTranslations(translations);
  const retailPrice = productPrice?.retailPrice;
  const isPriceDiscounted = Boolean(
    (productPrice?.discount?.discountPercentage ?? 0) > 0,
  );

  return (
    <Box
      padding="gutter"
      className={isSelected ? styles.upSellSelected : styles.upSellUnselected}
    >
      <Stack space="small">
        <Box display="flex" justifyContent="spaceBetween">
          <Heading level="3" align="left">
            {heading}
          </Heading>
          {price && (
            <Stack space={'none'} align={'right'}>
              {Boolean(priceDescription) && (
                <>
                  <Text size="small" weight="medium">
                    {priceDescription}
                  </Text>
                  <Box paddingBottom={'xsmall'} />
                </>
              )}
              <Inline space="xsmall" alignY={'center'}>
                {isPriceDiscounted && retailPrice && (
                  <s>
                    <PriceCurrencyTax
                      tax={tax}
                      size={'xsmall'}
                      originalPrice={retailPrice.value!}
                      currency={retailPrice.currency}
                    />
                  </s>
                )}
                <PriceCurrencyTax
                  priceWeight={'strong'}
                  currency={price.currency}
                  originalPrice={price.value}
                  tax={tax}
                />
              </Inline>
              {isPriceDiscounted ? (
                <Box
                  display="flex"
                  justifyContent="flexEnd"
                  paddingTop={'small'}
                >
                  <Badge tone="info">
                    {t('upsell-panel-discount-applied')}
                  </Badge>
                </Box>
              ) : (
                <Box paddingBottom={'small'} />
              )}
            </Stack>
          )}
        </Box>
        {divider ? <Divider weight="strong" /> : null}
        <Columns space="small">
          <Column width={columnWidth}>
            <Box paddingTop="medium" paddingBottom="large">
              <BenefitList items={benefits} space="large" />
            </Box>
            <Box paddingTop="small">
              <Inline space="none">
                <Button
                  data={{ testid: 'sto-upgrade-button' }}
                  onClick={onClick}
                  variant={isSelected ? 'ghost' : 'solid'}
                >
                  {isSelected
                    ? t('upsell-panel-remove-upgrade-button-label')
                    : t('upsell-panel-upgrade-button-label')}
                </Button>
              </Inline>
            </Box>
          </Column>
          <Column>
            <Box textAlign={['left', 'right']} paddingTop={'small'}>
              <Stack space="xsmall">
                <img
                  src={image}
                  alt={t('upsell-panel-image-alt')}
                  className={`${styles.image} ${imageClassName}`}
                />
                {imageDescription ? (
                  <Text size="xsmall" tone="secondary">
                    {imageDescription}
                  </Text>
                ) : null}
              </Stack>
            </Box>
          </Column>
        </Columns>
      </Stack>
    </Box>
  );
};
